<template>
    <EditComponent
        ref="refEditComponent"
        dialog-id="editSelectionModeExperienceDrawer"
        open-dialog-event="experiences:selection-mode"
        :open-dialog-post-function="handleDialogOpen"
        data-testid="profile-edition-experience-select-mode-drawer"
        confirm-data-testid="edit-manual-experience-confirm"
        :confirm-enabled="false"
        :is-visible-cta-confirm="false"
        size="default"
        :track-section-opened="EditionOpenedSection.EXPERIENCES"
    >
        <template #edit-header>{{ t('profile.experience.import.selection.title.v2') }}</template>
        <template #edit-body>
            <VJoyButtonCard
                align="left"
                class="resume-importer__selection-option"
                data-testid="profile-edition-experience-select-mode-resume-import"
                @click="selectResumeImport"
            >
                <VJoyWrapper direction="row" align="center" column-gap="4" wrap="nowrap" no-margin>
                    <VJoyIcon name="file-check" size="small" color="neutral-50" />
                    <div class="resume-importer__selection-option-content">
                        <VJoyText tag-name="p" preset="primary-450">
                            {{ t('profile.experience.import.selection.section.any_resume.title.v2') }}
                        </VJoyText>
                        <VJoyText tag-name="p" preset="primary-300" class="joy-mt-1 joy-color-neutral-40">
                            {{ t('profile.experience.import.selection.section.any_resume.subtitle.v2') }}
                        </VJoyText>
                    </div>
                    <VJoyTag
                        variant="secondary"
                        size="small"
                        :label="t('profile.experience.import.selection.section.any_resume.recommended')"
                        data-testid="profile-edition-experience-select-mode-recommended"
                    />
                </VJoyWrapper>
            </VJoyButtonCard>
            <VJoyButtonCard
                align="left"
                class="resume-importer__selection-option joy-mt-4"
                data-testid="profile-experience-select-mode-manual-addition"
                @click="selectManual"
            >
                <VJoyWrapper direction="row" align="center" column-gap="4" wrap="nowrap" no-margin>
                    <VJoyIcon name="pencil" size="small" color="neutral-50" />
                    <div class="resume-importer__selection-option-content">
                        <VJoyText tag-name="p" preset="primary-450">
                            {{ t('profile.experience.import.selection.section.manual.title.v2') }}
                        </VJoyText>
                        <VJoyText tag-name="p" preset="primary-300" class="joy-mt-1 joy-color-neutral-40">
                            {{ t('profile.experience.import.selection.section.manual.subtitle.v2') }}
                        </VJoyText>
                    </div>
                </VJoyWrapper>
            </VJoyButtonCard>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {onMounted, useRouter, useTranslation, navigateTo} from '#imports';
    import {VJoyButtonCard, VJoyIcon, VJoyTag, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import EditComponent from '~/components/edit/EditComponent.vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint, EditionOpenedSection} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';

    const {t} = useTranslation();
    const router = useRouter();
    const {profile} = storeToRefs(useProfileStore());

    const refEditComponent = useTemplateRef<{close: () => void}>('refEditComponent');
    const entryPoint = ref<EditionOpenedEntryPoint>(EditionOpenedEntryPoint.PROFILE_PAGE);

    function handleDialogOpen(payload?: {entryPoint?: EditionOpenedEntryPoint}): void {
        entryPoint.value = payload?.entryPoint ?? EditionOpenedEntryPoint.PROFILE_PAGE;
    }

    onMounted(() => {
        const route = router.currentRoute.value;
        if (Object.prototype.hasOwnProperty.call(route.query, 'editExperiences') && profile.value?.editMode) {
            eventBus.emit('experiences:selection-mode', {entryPoint: route.query.entryPoint as EditionOpenedEntryPoint});
            const queryParams = {...route.query};
            delete queryParams.editExperiences;
            if (
                queryParams.entryPoint === EditionOpenedEntryPoint.MESSENGER ||
                queryParams.entryPoint === EditionOpenedEntryPoint.SOURCING_PROJECT_APPLICATION
            ) {
                entryPoint.value = queryParams.entryPoint as EditionOpenedEntryPoint;
                delete queryParams.entryPoint;
            }
            router.replace({query: queryParams});
        }
    });

    function selectResumeImport(): void {
        refEditComponent.value?.close();
        eventBus.emit('experiences:resume-import-mode', {entryPoint: entryPoint.value});
        resetEntryPoint();
    }

    async function selectManual(): Promise<void> {
        await navigateTo({path: `/profile/experiences/new`, query: {entryPoint: entryPoint.value}});
    }

    function resetEntryPoint(): void {
        entryPoint.value = EditionOpenedEntryPoint.PROFILE_PAGE;
    }
</script>

<style lang="scss" scoped>
    .resume-importer {
        &__selection-option.joy-button-card {
            width: 100%;
            padding: var(--joy-core-spacing-4) var(--joy-core-spacing-8);
        }

        &__selection-option-content {
            flex: 1;
            min-width: 0;
        }
    }
</style>
